import { apiContractRequest } from "./client";

const jsonPost = (body) => ({ method: "POST", body });
const jsonPatch = (body) => ({ method: "PATCH", body });
const jsonDelete = () => ({ method: "DELETE" });

function buildOwnerImportFormData(file) {
  const formData = new FormData();
  formData.append("sourceType", "upload");
  formData.append("file", file);
  return formData;
}


export const ownerApi = {
  overview() {
    return apiContractRequest("/api/owner/overview");
  },

  center() {
    return apiContractRequest("/api/owner/center");
  },
  assistant({ month, currency = "ARS", tzOffsetMinutes = new Date().getTimezoneOffset() } = {}) {
    return apiContractRequest("/api/owner/assistant", { params: { month, currency, tzOffsetMinutes } });
  },
  jarvisTurn(payload = {}) {
    return apiContractRequest("/api/owner/jarvis/turn", jsonPost({
      ...payload,
      tzOffsetMinutes: payload.tzOffsetMinutes ?? new Date().getTimezoneOffset(),
    }));
  },
  calendar({ month, date, tzOffsetMinutes = new Date().getTimezoneOffset() } = {}) {
    return apiContractRequest("/api/owner/calendar", { params: { month, date, tzOffsetMinutes } });
  },
  updateCalendarSettings(payload) {
    return apiContractRequest("/api/owner/calendar/settings", jsonPatch(payload));
  },
  listRoutines({ includeInactive = true, limit = 150 } = {}) {
    return apiContractRequest("/api/owner/calendar/routines", { params: { includeInactive, limit } });
  },
  routineContext(routineId) {
    return apiContractRequest(`/api/owner/calendar/routines/${routineId}`);
  },
  createRoutine(payload) {
    return apiContractRequest("/api/owner/calendar/routines", jsonPost(payload));
  },
  updateRoutine(routineId, payload) {
    return apiContractRequest(`/api/owner/calendar/routines/${routineId}`, jsonPatch(payload));
  },
  setRoutineCheckin(routineId, date, completed = true) {
    return apiContractRequest(`/api/owner/calendar/routines/${routineId}/checkins/${encodeURIComponent(date)}`, jsonPatch({ completed }));
  },
  listLifeEntries({ status = "inbox", limit = 40 } = {}) {
    return apiContractRequest("/api/owner/life/entries", { params: { status, limit } });
  },
  createLifeEntry(payload) {
    return apiContractRequest("/api/owner/life/entries", jsonPost(payload));
  },
  updateLifeEntry(entryId, payload) {
    return apiContractRequest(`/api/owner/life/entries/${entryId}`, jsonPatch(payload));
  },
  listCommitments({ status = "open", limit = 80 } = {}) {
    return apiContractRequest("/api/owner/life/commitments", { params: { status, limit } });
  },
  createCommitment(payload) {
    return apiContractRequest("/api/owner/life/commitments", jsonPost(payload));
  },
  updateCommitment(commitmentId, payload) {
    return apiContractRequest(`/api/owner/life/commitments/${commitmentId}`, jsonPatch(payload));
  },
  listCommitmentTime(commitmentId) {
    return apiContractRequest(`/api/owner/life/commitments/${commitmentId}/time`);
  },
  addCommitmentTime(commitmentId, payload) {
    return apiContractRequest(`/api/owner/life/commitments/${commitmentId}/time`, jsonPost(payload));
  },
  listInitiatives({ status = "active", limit = 40 } = {}) {
    return apiContractRequest("/api/owner/life/initiatives", { params: { status, limit } });
  },
  createInitiative(payload) {
    return apiContractRequest("/api/owner/life/initiatives", jsonPost(payload));
  },
  getInitiative(initiativeId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}`);
  },
  initiativeContext(initiativeId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/context`);
  },
  projectDailyLoop(initiativeId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/daily-loop`);
  },
  answerProjectDailyLoop(initiativeId, payload) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/daily-loop/answer`, jsonPost(payload));
  },
  initiativeEconomics(initiativeId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/economics`);
  },
  initiativeProductHealth(initiativeId, { days = 30 } = {}) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/product-health`, { params: { days } });
  },
  updateInitiative(initiativeId, payload) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}`, jsonPatch(payload));
  },
  deleteInitiative(initiativeId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}`, { method: "DELETE" });
  },
  projectAgreement(initiativeId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/agreement`);
  },
  syncProjectWebProfitBilling(initiativeId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/agreement/webprofit-billing/sync`, jsonPost({}));
  },
  createProjectTerm(initiativeId, payload) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/agreement/terms`, jsonPost(payload));
  },
  updateProjectTerm(initiativeId, termId, payload) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/agreement/terms/${termId}`, jsonPatch(payload));
  },
  createProjectPaymentPlan(initiativeId, payload) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/agreement/payments`, jsonPost(payload));
  },
  updateProjectPaymentPlan(initiativeId, paymentPlanId, payload) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/agreement/payments/${paymentPlanId}`, jsonPatch(payload));
  },
  reconcileProjectPaymentPlan(initiativeId, paymentPlanId, transactionId) {
    return apiContractRequest(`/api/owner/life/initiatives/${initiativeId}/agreement/payments/${paymentPlanId}/reconcile`, jsonPost({ transactionId }));
  },
  bulkLinkProjectPayments(payload) {
    return apiContractRequest("/api/owner/finance/transactions/bulk-project", jsonPost(payload));
  },

  memories({ from, to, limit = 280, tzOffsetMinutes = new Date().getTimezoneOffset() } = {}) {
    return apiContractRequest("/api/owner/memories", { params: { from, to, limit, tzOffsetMinutes } });
  },
  navigationLearning({ limit = 400 } = {}) {
    return apiContractRequest("/api/owner/navigation-learning", { params: { limit } });
  },
  recordNavigationLearning(payload) {
    return apiContractRequest("/api/owner/navigation-learning/feedback", jsonPost(payload));
  },

  listFinanceAccounts() {
    return apiContractRequest("/api/owner/finance/accounts");
  },
  createFinanceAccount(payload) {
    return apiContractRequest("/api/owner/finance/accounts", jsonPost(payload));
  },
  updateFinanceAccount(accountId, payload) {
    return apiContractRequest(`/api/owner/finance/accounts/${accountId}`, jsonPatch(payload));
  },
  listFinanceCategories({ includeArchived = false } = {}) {
    return apiContractRequest("/api/owner/finance/categories", { params: { includeArchived } });
  },
  createFinanceCategory(payload) {
    return apiContractRequest("/api/owner/finance/categories", jsonPost(payload));
  },
  updateFinanceCategory(categoryId, payload) {
    return apiContractRequest(`/api/owner/finance/categories/${categoryId}`, jsonPatch(payload));
  },
  listFinanceTransactions({ limit = 80, type = "", category = "", from = "", to = "", initiativeId = "", projectTermId = "" } = {}) {
    return apiContractRequest("/api/owner/finance/transactions", {
      params: { limit, ...(type ? { type } : {}), ...(category ? { category } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}), ...(initiativeId ? { initiativeId } : {}), ...(projectTermId ? { projectTermId } : {}) },
    });
  },
  financeMovementOptions() {
    return apiContractRequest("/api/owner/finance/movement-options");
  },
  createFinanceTransaction(payload) {
    return apiContractRequest("/api/owner/finance/transactions", jsonPost(payload));
  },
  updateFinanceTransaction(transactionId, payload) {
    return apiContractRequest(`/api/owner/finance/transactions/${transactionId}`, jsonPatch(payload));
  },
  listInventoryItems({ query = "", area = "", limit = 100 } = {}) {
    return apiContractRequest("/api/owner/inventory/items", { params: { ...(query ? { query } : {}), ...(area ? { area } : {}), limit } });
  },
  listInventoryMovements({ limit = 60 } = {}) {
    return apiContractRequest("/api/owner/inventory/movements", { params: { limit } });
  },
  createInventoryMovement(payload) {
    return apiContractRequest("/api/owner/inventory/movements", jsonPost(payload));
  },
  listRecurringFinance({ includeEnded = false } = {}) {
    return apiContractRequest("/api/owner/finance/recurring", { params: { includeEnded } });
  },
  createRecurringFinance(payload) {
    return apiContractRequest("/api/owner/finance/recurring", jsonPost(payload));
  },
  updateRecurringFinance(planId, payload) {
    return apiContractRequest(`/api/owner/finance/recurring/${planId}`, jsonPatch(payload));
  },
  listRecurringOccurrences(planId, { near = "", limit = 24 } = {}) {
    return apiContractRequest(`/api/owner/finance/recurring/${planId}/occurrences`, { params: { ...(near ? { near } : {}), limit } });
  },
  updateRecurringOccurrence(planId, periodKey, payload) {
    return apiContractRequest(`/api/owner/finance/recurring/${planId}/occurrences/${encodeURIComponent(periodKey)}`, jsonPatch(payload));
  },
  recordRecurringFinance(planId, payload) {
    return apiContractRequest(`/api/owner/finance/recurring/${planId}/record`, jsonPost(payload));
  },
  linkFinanceTransactionRecurring(transactionId, payload) {
    return apiContractRequest(`/api/owner/finance/transactions/${transactionId}/recurring`, jsonPost(payload));
  },
  listInfrastructure({ includeEnded = false } = {}) {
    return apiContractRequest("/api/owner/finance/infrastructure", { params: { includeEnded } });
  },
  createInfrastructure(payload) {
    return apiContractRequest("/api/owner/finance/infrastructure", jsonPost(payload));
  },
  updateInfrastructure(poolId, payload) {
    return apiContractRequest(`/api/owner/finance/infrastructure/${poolId}`, jsonPatch(payload));
  },
  endInfrastructure(poolId) {
    return apiContractRequest(`/api/owner/finance/infrastructure/${poolId}`, jsonDelete());
  },
  createFinanceImport(file) {
    return apiContractRequest("/api/owner/finance/imports", {
      method: "POST",
      body: buildOwnerImportFormData(file),
    });
  },
  previewFinanceImport(importId) {
    return apiContractRequest(`/api/owner/finance/imports/${encodeURIComponent(importId)}/preview`, jsonPost({}));
  },
  commitFinanceImport(importId) {
    return apiContractRequest(`/api/owner/finance/imports/${encodeURIComponent(importId)}/commit`, jsonPost({}));
  },

  commercialSummary() {
    return apiContractRequest("/api/owner/commercial/summary");
  },
  listClients({ status = "", query = "", limit = 250 } = {}) {
    return apiContractRequest("/api/owner/commercial/clients", { params: { ...(status ? { status } : {}), ...(query ? { query } : {}), limit } });
  },
  createClient(payload) {
    return apiContractRequest("/api/owner/commercial/clients", jsonPost(payload));
  },
  updateClient(clientId, payload) {
    return apiContractRequest(`/api/owner/commercial/clients/${clientId}`, jsonPatch(payload));
  },
  searchWebProfitUsers({ query = "", limit = 20 } = {}) {
    return apiContractRequest("/api/owner/webprofit/users", { params: { query, limit } });
  },
  searchWebProfitProjects({ query = "", limit = 20 } = {}) {
    return apiContractRequest("/api/owner/webprofit/projects", { params: { query, limit } });
  },
  clientWebProfitContext(clientId) {
    return apiContractRequest(`/api/owner/people/${clientId}/webprofit`);
  },
  linkClientWebProfitUser(clientId, userId) {
    return apiContractRequest(`/api/owner/people/${clientId}/webprofit`, jsonPatch({ userId: userId || null }));
  },
  linkClientWebProfitProject(clientId, projectId, linked = true) {
    return apiContractRequest(`/api/owner/people/${clientId}/webprofit/projects/${projectId}`, jsonPatch({ linked }));
  },
  peopleFocus({ limit = 20 } = {}) {
    return apiContractRequest("/api/owner/people/focus", { params: { limit } });
  },
  personContext(clientId) {
    return apiContractRequest(`/api/owner/people/${clientId}/context`);
  },
  clientChatGptExport(clientId) {
    return apiContractRequest(`/api/owner/people/${clientId}/chatgpt-export`);
  },
  listInteractions(clientId, { limit = 60 } = {}) {
    return apiContractRequest(`/api/owner/people/${clientId}/interactions`, { params: { limit } });
  },
  createInteraction(clientId, payload) {
    return apiContractRequest(`/api/owner/people/${clientId}/interactions`, jsonPost(payload));
  },
  listServices({ includeInactive = true } = {}) {
    return apiContractRequest("/api/owner/commercial/services", { params: { includeInactive } });
  },
  createService(payload) {
    return apiContractRequest("/api/owner/commercial/services", jsonPost(payload));
  },
  updateService(serviceId, payload) {
    return apiContractRequest(`/api/owner/commercial/services/${serviceId}`, jsonPatch(payload));
  },
  listSales({ limit = 120, status = "" } = {}) {
    return apiContractRequest("/api/owner/commercial/sales", {
      params: { limit, ...(status ? { status } : {}) },
    });
  },
  createSale(payload) {
    return apiContractRequest("/api/owner/commercial/sales", jsonPost(payload));
  },
  updateSaleStatus(saleId, status) {
    return apiContractRequest(`/api/owner/commercial/sales/${saleId}/status`, jsonPatch({ status }));
  },
  listSalePayments(saleId) {
    return apiContractRequest(`/api/owner/commercial/sales/${saleId}/payments`);
  },
  createSalePayment(saleId, payload) {
    return apiContractRequest(`/api/owner/commercial/sales/${saleId}/payments`, jsonPost(payload));
  },
};
